import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { CustomFieldValueDto } from '@/types/CustomFieldValueDto.ts';
import { AddressDto } from '@/types/AddressDto.ts';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';
import { PriceSource } from '@/features/dashboard/bookingChanges/types/dto/PriceSource.ts';
import {
  InsuranceChangeOp,
  OptionChangeOp,
  TransportChangeOp,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeOps.ts';

/**
 * Dyskryminator pozycji paczki — odpowiada `@JsonSubTypes` na `ChangeItemDto`
 * po stronie backendu (pole `type` w JSON-ie).
 */
export enum ChangeItemType {
  PARTICIPANT_DATA = 'PARTICIPANT_DATA',
  OPTION = 'OPTION',
  CLIENT_DATA = 'CLIENT_DATA',
  CUSTOM_VALUES = 'CUSTOM_VALUES',
  TRANSPORT = 'TRANSPORT',
  BASE_PRICE = 'BASE_PRICE',
  INSURANCE = 'INSURANCE',
  WITHDRAWAL = 'WITHDRAWAL',
  MANUAL_LINE = 'MANUAL_LINE',
}

interface BaseChangeItemDto {
  id: string;
  /** `null` dla pozycji nieprzypisanych do uczestnika (CLIENT_DATA, WITHDRAWAL). */
  participantId: string | null;
}

/**
 * Zmiana danych uczestnika, również cesja. Niesie wyłącznie stan docelowy —
 * wartości „przed” trzeba dobrać z rachunku rezerwacji.
 */
export interface ParticipantDataChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.PARTICIPANT_DATA;
  newFirstName: string | null;
  newLastName: string | null;
  /** `YYYY-MM-DD`. */
  newBirthDate: string | null;
}

/** Zmiana opcji wyjazdu (warsztaty, wycieczki, nocleg, sprzęt…). */
export interface OptionChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.OPTION;
  op: OptionChangeOp;
  tourOptionId: string;
  removedVariantId: string | null;
  newVariantId: string | null;
  priceSource: PriceSource;
  /** Wypełnione tylko przy `priceSource === MANUAL`. */
  priceOverride: MoneyValueDto | null;
  priceNote: string | null;
}

/** Cały zestaw kontaktowy zapisującego to jedna pozycja paczki. */
export interface ClientDataChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.CLIENT_DATA;
  participantId: null;
  newFirstName: string | null;
  newLastName: string | null;
  newEmail: string | null;
  newPhoneNumber: PhoneNumberDto | null;
  /** `YYYY-MM-DD`. */
  newBirthDate: string | null;
  newAddress: AddressDto | null;
}

/** Pełny nowy zestaw wartości pól niestandardowych uczestnika (`fieldId` → wartość). */
export interface ParticipantCustomValuesChangeItemDto
  extends BaseChangeItemDto {
  type: ChangeItemType.CUSTOM_VALUES;
  newCustomValues: Record<string, CustomFieldValueDto> | null;
}

/** Zmiana wariantu transportu uczestnika; `CLEAR` ⇒ `newVariantId === null`. */
export interface TransportChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.TRANSPORT;
  op: TransportChangeOp;
  previousVariantId: string | null;
  newVariantId: string | null;
  priceSource: PriceSource;
  priceOverride: MoneyValueDto | null;
  priceNote: string | null;
}

/** Nadpisanie ceny bazowej uczestnika — zawsze ręczne, więc bez `priceSource`. */
export interface BasePriceChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.BASE_PRICE;
  priceOverride: MoneyValueDto | null;
  priceNote: string | null;
}

export interface InsuranceChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.INSURANCE;
  op: InsuranceChangeOp;
  previousInsuranceId: string | null;
  newInsuranceId: string | null;
}

/** Rezygnacja uczestników; `justification` to uzasadnienie z art. 47. */
export interface WithdrawalChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.WITHDRAWAL;
  participantId: null;
  withdrawnParticipantIds: string[];
  justification: string;
  /** `YYYY-MM-DD`. */
  refundDueDate: string;
}

/**
 * Wolna linia rachunku dopisana przez operatora.
 *
 * Jako jedyna pozycja może nie mieć uczestnika: `participantId === null` znaczy
 * wtedy całą rezerwację (reguła D10). `priceSource` jest zawsze `MANUAL`, więc
 * — tak jak w `BasePriceChangeItemDto` — DTO tego pola nie wystawia.
 */
export interface ManualLineChangeItemDto extends BaseChangeItemDto {
  type: ChangeItemType.MANUAL_LINE;
  /** Wymagany i niepusty: pozycja jako jedyna nie ma nazwy ze snapshotu. */
  description: string;
  /** Jedyna cena pozycji: dodatnia = koszt, ujemna = zniżka. */
  priceOverride: MoneyValueDto;
  priceNote: string | null;
}

export type ChangeItemDto =
  | ParticipantDataChangeItemDto
  | OptionChangeItemDto
  | ClientDataChangeItemDto
  | ParticipantCustomValuesChangeItemDto
  | TransportChangeItemDto
  | BasePriceChangeItemDto
  | InsuranceChangeItemDto
  | WithdrawalChangeItemDto
  | ManualLineChangeItemDto;

/** `items.filter(...)` samo z siebie nie zawęża typu union do wariantu. */
export const isChangeItemOfType =
  <T extends ChangeItemType>(type: T) =>
  (item: ChangeItemDto): item is Extract<ChangeItemDto, { type: T }> =>
    item.type === type;
